const ACCESS_STORAGE_KEY = 'flux_prompt_sample_access_key';

const PRODUCTION_API_PATH = '/enterprise-makeup-api/api/flux-reference-transfer';
const LEGACY_API_PATH = '/flux-reference-transfer-api';
const DEV_MAKEUP_PORT = '5014';

export function getStoredAccessKey() {
  try {
    return String(sessionStorage.getItem(ACCESS_STORAGE_KEY) || '').trim();
  } catch {
    return '';
  }
}

export function setStoredAccessKey(key) {
  try {
    sessionStorage.setItem(ACCESS_STORAGE_KEY, String(key || '').trim());
  } catch {
    // ignore
  }
}

export function clearStoredAccessKey() {
  try {
    sessionStorage.removeItem(ACCESS_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function getFluxReferenceTransferApiBaseUrl() {
  const fromEnv =
    typeof process !== 'undefined' &&
    process.env &&
    process.env.REACT_APP_FLUX_REFERENCE_TRANSFER_API_URL;
  if (fromEnv && String(fromEnv).trim()) {
    return String(fromEnv).trim().replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && window.location) {
    const { origin, hostname, port } = window.location;
    const isLocal =
      hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
    if (isLocal && (port === '3000' || port === '')) {
      return `http://localhost:${DEV_MAKEUP_PORT}/api/flux-reference-transfer`;
    }
    return `${origin}${PRODUCTION_API_PATH}`;
  }
  return `http://localhost:${DEV_MAKEUP_PORT}/api/flux-reference-transfer`;
}

async function parseJsonResponse(res) {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    const err = new Error(
      res.ok
        ? 'Invalid JSON from server'
        : `Request failed (${res.status}). API may be unreachable — check nginx.`,
    );
    err.status = res.status;
    err.raw = text.slice(0, 200);
    throw err;
  }
}

function sampleHeaders(extra = {}) {
  const key = getStoredAccessKey();
  const headers = { ...extra };
  if (key) headers['X-Flux-Prompt-Sample-Key'] = key;
  return headers;
}

async function fetchBuildInfoAt(base) {
  const res = await fetch(`${base}/build-info`);
  const data = await parseJsonResponse(res);
  if (!res.ok || data?.success === false) {
    throw new Error(data?.error || data?.code || `Request failed (${res.status})`);
  }
  return data;
}

export async function fetchFluxReferenceTransferBuildInfo() {
  const primary = getFluxReferenceTransferApiBaseUrl();
  try {
    return await fetchBuildInfoAt(primary);
  } catch (primaryErr) {
    if (typeof window === 'undefined' || !window.location) throw primaryErr;
    const legacy = `${window.location.origin}${LEGACY_API_PATH}`;
    if (legacy === primary) throw primaryErr;
    return fetchBuildInfoAt(legacy);
  }
}

export async function verifyFluxReferenceTransferAccess(accessKey) {
  const bases = [getFluxReferenceTransferApiBaseUrl()];
  if (typeof window !== 'undefined' && window.location) {
    const legacy = `${window.location.origin}${LEGACY_API_PATH}`;
    if (!bases.includes(legacy)) bases.push(legacy);
  }
  let lastErr;
  for (const base of bases) {
    const res = await fetch(`${base}/verify-access`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ access_key: accessKey }),
    });
    let data;
    try {
      data = await parseJsonResponse(res);
    } catch (e) {
      lastErr = e;
      continue;
    }
    if (!res.ok || data?.success === false) {
      const err = new Error(data?.error || data?.code || `Request failed (${res.status})`);
      err.code = data?.code;
      throw err;
    }
    setStoredAccessKey(accessKey);
    return data;
  }
  throw lastErr || new Error('Request failed');
}

export async function generateFluxReferenceTransfer({
  image,
  referenceImage,
  prompt,
  aspects,
  preserveFaceMode,
  transferScope,
  transferPose,
  seed,
  steps,
  guidance,
}) {
  const body = {
    image,
    reference_image: referenceImage,
    transfer_aspects: aspects,
    prompt: prompt || '',
  };
  if (preserveFaceMode) body.preserve_face_mode = preserveFaceMode;
  if (transferScope) body.transfer_scope = transferScope;
  if (transferPose != null) body.transfer_pose = Boolean(transferPose);
  body.transfer_fidelity = 'strict';
  if (seed != null) body.seed = seed;
  if (steps != null) body.steps = steps;
  if (guidance != null) body.guidance = guidance;
  const res = await fetch(`${getFluxReferenceTransferApiBaseUrl()}/generate`, {
    method: 'POST',
    headers: sampleHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.success === false) {
    const err = new Error(data?.error || data?.code || `Request failed (${res.status})`);
    err.code = data?.code;
    throw err;
  }
  return data;
}
